import { ChevronDown, ChevronRight } from "lucide-react";
import { Fragment, useMemo, useState } from "react";

import { useCatalogue } from "@/api/catalogue";
import type { CatalogueRow } from "@/api/types";
import { EntryDetail } from "@/components/catalogue/EntryDetail";
import { viewFromCatalogueRow } from "@/components/catalogue/entryDetailAdapter";
import { OwningJobCell } from "@/components/catalogue/OwningJobCell";
import {
  ariaSortFor,
  defaultSortDir,
  SortableHeader,
  sortRows,
  useCycleSort,
  useExpandedIds,
} from "@/components/catalogue/sort";
import { EmptyState } from "@/components/EmptyState";
import { ErrorBanner } from "@/components/ErrorBanner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

/**
 * `/catalogue` — browseable list of every promoted row in
 * `tools_and_services`. Filtering (category, status, stale-window) +
 * sorting (name / last_updated) + search (substring on `name`) all run
 * client-side over the in-memory rows; the catalogue is small enough at
 * M1 (<200 rows expected) that no pagination or server-side filtering
 * is justified yet. The AC for issue #218 is "performance acceptable
 * for ~1000" — a render-budget bar, not a virtualisation requirement.
 *
 * Each row expands via a leading chevron button (keyboard-accessible)
 * into `<EntryDetail>` — shared with the drafts page and the HITL
 * review-row card — showing every Sheet column, the curator's
 * embedding text in its own bordered section, and a "Show raw JSON"
 * toggle for debugging. See issue #218 for the operator-feedback
 * rationale.
 */

const STALE_SOON_THRESHOLD_DAYS = 14;
const COLUMN_COUNT = 9;

type StaleFilter = "all" | "stale_soon" | "stale";

export function Catalogue() {
  const query = useCatalogue();
  const [category, setCategory] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");
  const [staleFilter, setStaleFilter] = useState<StaleFilter>("all");
  const [search, setSearch] = useState<string>("");
  const { sortKey, sortDir, cycle: cycleSort } = useCycleSort(
    "last_updated",
    "desc",
    defaultSortDir,
  );
  const { expandedIds, toggle: toggleExpanded } = useExpandedIds();

  const distinctCategories = useMemo(
    () => distinctValues(query.data?.rows, "category"),
    [query.data?.rows],
  );
  const distinctStatuses = useMemo(
    () => distinctValues(query.data?.rows, "status"),
    [query.data?.rows],
  );

  const filteredSorted = useMemo(() => {
    if (!query.data) return [];
    const needle = search.trim().toLowerCase();
    const matched = query.data.rows.filter((row) => {
      if (category !== "all" && String(row.category ?? "") !== category) return false;
      if (status !== "all" && String(row.status ?? "") !== status) return false;
      // Treat "unknown freshness" (null `last_updated`) as effectively
      // fresh — they shouldn't surface in stale or stale-soon filters.
      const days = row.daysUntilStale ?? Number.POSITIVE_INFINITY;
      if (staleFilter === "stale" && days > 0) return false;
      if (staleFilter === "stale_soon" && (days > STALE_SOON_THRESHOLD_DAYS || days <= 0)) {
        return false;
      }
      if (needle.length > 0) {
        const name = String(row.name ?? "").toLowerCase();
        if (!name.includes(needle)) return false;
      }
      return true;
    });
    return sortRows(matched, sortKey, sortDir);
  }, [query.data, category, status, staleFilter, search, sortKey, sortDir]);

  if (query.isPending) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (query.isError) {
    return <ErrorBanner error={query.error} onRetry={() => void query.refetch()} />;
  }

  const rows = query.data.rows;
  if (rows.length === 0) {
    return (
      <EmptyState
        title="Catalogue is empty"
        description="No rows have been promoted to tools_and_services yet. Run the curator → validator → review cycle to add some."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <p className="text-sm text-text-muted">
          {rows.length} row{rows.length === 1 ? "" : "s"} ·{" "}
          stale after {query.data.staleThresholdDays} days
        </p>
        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name…"
          className="w-[220px]"
          aria-label="Search by name"
        />
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {distinctCategories.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {distinctStatuses.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1">
          <FilterPill
            active={staleFilter === "all"}
            onClick={() => setStaleFilter("all")}
          >
            All
          </FilterPill>
          <FilterPill
            active={staleFilter === "stale_soon"}
            onClick={() => setStaleFilter("stale_soon")}
          >
            Stale soon
          </FilterPill>
          <FilterPill
            active={staleFilter === "stale"}
            onClick={() => setStaleFilter("stale")}
          >
            Stale
          </FilterPill>
        </div>
      </div>

      {filteredSorted.length === 0 ? (
        <EmptyState
          title="No rows match"
          description="Adjust the filters above to widen the result set."
        />
      ) : (
        <div className="border border-border rounded bg-bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>ID</TableHead>
                <TableHead aria-sort={ariaSortFor(sortKey === "name", sortDir)}>
                  <SortableHeader
                    label="Name"
                    active={sortKey === "name"}
                    dir={sortDir}
                    onClick={() => cycleSort("name")}
                  />
                </TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Status</TableHead>
                <TableHead
                  aria-sort={ariaSortFor(sortKey === "last_updated", sortDir)}
                >
                  <SortableHeader
                    label="Last updated"
                    active={sortKey === "last_updated"}
                    dir={sortDir}
                    onClick={() => cycleSort("last_updated")}
                  />
                </TableHead>
                <TableHead>Stale in</TableHead>
                <TableHead>Owning job</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSorted.map((row) => {
                const id = String(row.id);
                const expanded = expandedIds.has(id);
                return (
                  <Fragment key={id}>
                    <TableRow data-testid={`catalogue-row-${id}`}>
                      <TableCell className="w-8 p-0">
                        <ExpandToggleButton
                          expanded={expanded}
                          onToggle={() => toggleExpanded(id)}
                          rowLabel={String(row.name ?? id)}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-xs text-accent">
                        {id}
                      </TableCell>
                      <TableCell>{String(row.name ?? "")}</TableCell>
                      <TableCell className="text-sm text-text-muted">
                        {String(row.vendor ?? "")}
                      </TableCell>
                      <TableCell className="text-sm">{String(row.category ?? "")}</TableCell>
                      <TableCell className="text-sm">{String(row.status ?? "")}</TableCell>
                      <TableCell className="text-xs text-text-muted">
                        {String(row.last_updated ?? "—")}
                      </TableCell>
                      <TableCell>
                        <StaleCell row={row} />
                      </TableCell>
                      <TableCell className="text-xs">
                        <OwningJobCell
                          jobId={row.jobId}
                          jobNumber={row.jobNumber}
                          emptyVariant="dash"
                        />
                      </TableCell>
                    </TableRow>
                    {expanded ? (
                      <TableRow data-testid={`catalogue-row-${id}-expanded`}>
                        <TableCell
                          colSpan={COLUMN_COUNT}
                          className="bg-bg/40 p-4"
                        >
                          <EntryDetail view={viewFromCatalogueRow(row)} raw={row} />
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

/**
 * Dedicated chevron button used as the expand/collapse affordance on
 * every row. Keyboard-focusable, native button semantics — Enter /
 * Space activate it. Lifted into its own cell so the row's other
 * cells (in particular the Owning-job link on the drafts page)
 * don't need `stopPropagation` to escape a row-level click handler.
 */
export function ExpandToggleButton({
  expanded,
  onToggle,
  rowLabel,
}: {
  expanded: boolean;
  onToggle: () => void;
  rowLabel: string;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={onToggle}
      aria-expanded={expanded}
      aria-label={expanded ? `Collapse ${rowLabel}` : `Expand ${rowLabel}`}
      className="h-9 w-9 text-text-muted"
    >
      {expanded ? (
        <ChevronDown className="h-4 w-4" />
      ) : (
        <ChevronRight className="h-4 w-4" />
      )}
    </Button>
  );
}

function StaleCell({ row }: { row: CatalogueRow }) {
  const days = row.daysUntilStale;
  if (days === null) return <span className="text-text-faint">—</span>;
  if (days <= 0) {
    return <Badge variant="danger">stale ({Math.abs(days)}d overdue)</Badge>;
  }
  if (days <= STALE_SOON_THRESHOLD_DAYS) {
    return <Badge variant="accent">in {days}d</Badge>;
  }
  return <span className="text-sm text-text-muted">in {days}d</span>;
}

function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-3 py-1.5 text-xs font-mono uppercase tracking-label rounded border transition-colors",
        active
          ? "border-accent bg-accent-dim text-accent"
          : "border-border-light bg-bg-card text-text-muted hover:text-text",
      )}
    >
      {children}
    </button>
  );
}

/**
 * Distinct, alphabetically sorted, non-empty values for `key` across
 * `rows`. Used to populate the category / status `<Select>` dropdowns
 * from the response — keeps the filter dropdowns in sync with the
 * data without a separate config endpoint.
 */
function distinctValues(
  rows: ReadonlyArray<CatalogueRow> | undefined,
  key: string,
): string[] {
  if (!rows) return [];
  const set = new Set<string>();
  for (const row of rows) {
    const v = row[key];
    if (typeof v === "string" && v.length > 0) set.add(v);
  }
  return [...set].sort();
}
